import {useLocation, useNavigate} from 'react-router-dom';
import {useCallback} from 'react';

export const useParamState = <T extends Record<string, string | undefined>>(): [
  Partial<T>,
  (k: string, v?: string) => void,
] => {
  const navigate = useNavigate();
  const location = useLocation();
  const values = Object.fromEntries(
    new URLSearchParams(location.search),
  ) as Partial<T>;

  const setParam = useCallback(
    (key: string, value?: string) => {
      const params = new URLSearchParams(location.search);
      if (value === undefined) {
        params.delete(key);
      } else {
        params.set(key, encodeURI(value));
      }
      navigate({
        pathname: location.pathname,
        search: params.toString(),
        hash: location.hash,
      });
    },
    [location.hash, location.pathname, location.search, navigate],
  );

  return [values, setParam];
};

export const useParamIndex = (
  key: string,
  initial: number,
): [number, (index: number) => void] => {
  const [params, setParam] = useParamState();
  const param = params[key];
  const value = param ? parseInt(param) : initial;
  const setValue = useCallback(
    (index: number) => setParam(key, `${index}`),
    [key, setParam],
  );
  return [value, setValue];
};
